import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Car, Plus, Trash2, X } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const modelsText = (m) => (m ? Object.entries(m).map(([k, v]) => `${k} ${v}`).join(" · ") : "");

// Admin › Dealers cell: current test-drive cars (or an amber "Set TD cars" until captured).
export function TdCarsCell({ dealer, onOpen }) {
  const t = dealer.td_cars || {};
  return (
    <button onClick={onOpen} data-testid={`dealer-tdcars-${dealer.code}`} title={modelsText(t.models) || undefined}
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs transition-colors duration-200 hover:bg-secondary ${t.needs_td_cars ? "border-amber-400 bg-amber-50 text-amber-900" : ""}`}>
      <Car size={12} />
      {t.td_set ? <span className="num font-semibold">{t.td_cars} TD car{t.td_cars === 1 ? "" : "s"}</span> : "Set TD cars"}
    </button>
  );
}

function ModelSplit({ rows, setRows, models }) {
  const used = new Set(rows.map((r) => r.model));
  const options = models.filter((m) => !used.has(m));
  return (
    <div className="space-y-1.5" data-testid="td-model-split">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-muted-foreground">Model-wise breakdown (optional)</label>
        <button type="button" onClick={() => options.length && setRows([...rows, { model: options[0], n: "" }])} disabled={!options.length} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-40" data-testid="td-model-add"><Plus size={12} /> Add model</button>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`td-model-row-${i}`}>
          <Select value={r.model} onValueChange={(v) => setRows(rows.map((x, j) => (j === i ? { ...x, model: v } : x)))}>
            <SelectTrigger className="h-8 text-xs flex-1" data-testid={`td-model-select-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{[r.model, ...options].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" min={0} max={500} value={r.n} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, n: e.target.value } : x)))} className="h-8 w-20 text-sm" placeholder="cars" data-testid={`td-model-n-${i}`} />
          <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-600" data-testid={`td-model-remove-${i}`}><X size={13} /></button>
        </div>
      ))}
      {rows.length === 0 && <p className="text-[11px] text-muted-foreground">No breakdown — only the total metric is computed.</p>}
    </div>
  );
}

export function TdCarsDialog({ dealer, onClose, onSaved }) {
  const [info, setInfo] = useState(null);
  const [models, setModels] = useState([]);
  const [form, setForm] = useState({ td_cars: "", effective_from: today(), note: "" });
  const [split, setSplit] = useState([]);
  const [saving, setSaving] = useState(false);
  const code = dealer?.code;

  useEffect(() => { api.get("/dpc/meta").then(({ data }) => setModels((data.models || []).filter((m) => m.active).map((m) => m.name))).catch(() => {}); }, []);
  useEffect(() => {
    if (!code) return;
    setInfo(null);
    api.get(`/admin/dealers/${encodeURIComponent(code)}/td-cars`).then(({ data }) => {
      setInfo(data);
      setForm({ td_cars: data.current?.td_cars ?? "", effective_from: today(), note: "" });
      setSplit(Object.entries(data.current?.models || {}).map(([model, n]) => ({ model, n })));
    }).catch((e) => toast.error(apiError(e)));
  }, [code]);

  const splitSum = split.reduce((s, r) => s + Number(r.n || 0), 0);
  const total = Number(form.td_cars || 0);
  const overTotal = splitSum > total;

  const save = async () => {
    setSaving(true);
    try {
      const body = { td_cars: total, effective_from: form.effective_from, note: form.note, models: split.length ? Object.fromEntries(split.filter((r) => Number(r.n || 0) > 0).map((r) => [r.model, Number(r.n)])) : null };
      const { data } = await api.put(`/admin/dealers/${encodeURIComponent(code)}/td-cars`, body);
      setInfo((i) => ({ ...i, current: data.current, history: data.history }));
      setForm((f) => ({ ...f, note: "" }));
      toast.success(`${code} test-drive cars saved from ${form.effective_from}`);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (eff) => {
    if (!window.confirm(`Remove the test-drive car record effective ${eff}?`)) return;
    try {
      await api.delete(`/admin/dealers/${encodeURIComponent(code)}/td-cars/${eff}`);
      const { data: fresh } = await api.get(`/admin/dealers/${encodeURIComponent(code)}/td-cars`);
      setInfo(fresh);
      toast.success("Record removed");
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const cur = info?.current;
  return (
    <Dialog open={!!dealer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="tdcars-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Test-drive cars — {code} {dealer?.name}</DialogTitle>
          <DialogDescription className="text-xs">Number of test-drive vehicles stationed at this dealer code. Effective-dated: earlier months keep the fleet that existed then. The total always governs; the model-wise split only adds per-model figures.</DialogDescription>
        </DialogHeader>
        {cur && (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid="tdcars-current">
            <span>Today: <b className="num" data-testid="tdcars-current-total">{cur.td_cars ?? "—"} TD cars</b>{cur.models && <span className="text-muted-foreground"> · {modelsText(cur.models)}</span>}</span>
            {cur.effective_from && <span className="text-muted-foreground">in force since {cur.effective_from}</span>}
            {cur.needs_td_cars && <span className="text-amber-700 font-semibold">TD cars not set</span>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">Test-drive cars (total)</label>
            <Input type="number" min={0} max={500} value={form.td_cars} onChange={(e) => setForm({ ...form, td_cars: e.target.value })} className="h-9 text-sm" data-testid="tdcars-total-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">Effective from</label>
            <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className="h-9 text-sm" data-testid="tdcars-effective-input" />
          </div>
        </div>
        <ModelSplit rows={split} setRows={setSplit} models={models} />
        {overTotal && <p className="text-[11px] text-red-600" data-testid="tdcars-split-error">Model-wise cars ({splitSum}) exceed the total ({total}).</p>}
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Note (optional)</label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Syros TD car added" className="h-9 text-sm" data-testid="tdcars-note-input" />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">Saving on an existing date replaces that record.</span>
          <Button onClick={save} disabled={saving || !form.effective_from || total === 0 || overTotal} data-testid="tdcars-save-btn">Save TD cars</Button>
        </div>
        <div className="border rounded-md overflow-auto max-h-56" data-testid="tdcars-history">
          <table className="w-full text-xs">
            <thead className="border-b bg-muted/40">
              <tr>{["Effective from", "TD cars", "Model-wise", "Note", "By", ""].map((h) => <th key={h} className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(info?.history || []).map((r) => (
                <tr key={r.effective_from} className="border-b last:border-0" data-testid={`tdcars-history-${r.effective_from}`}>
                  <td className="px-2 py-1.5 num font-medium">{r.effective_from}</td>
                  <td className="px-2 py-1.5 num">{r.td_cars}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{modelsText(r.models) || "—"}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{r.note || "—"}</td>
                  <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[120px]" title={r.by}>{r.by}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => remove(r.effective_from)} className="text-muted-foreground hover:text-red-600 transition-colors duration-200" data-testid={`tdcars-delete-${r.effective_from}`}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
              {info && !info.history?.length && <tr><td colSpan={6} className="px-2 py-3 text-center text-muted-foreground" data-testid="tdcars-history-empty">No records yet</td></tr>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
